/**
 * "Review your draft" — the one step where the client is the bottleneck and
 * knows it. Two exits only: approve, or say what's wrong. Change notes are
 * delivered by mail, so a note never sits unread in a table nobody built yet.
 */

import React, { useState } from 'react';
import { Panel, PanelBody, PanelHeader } from '../../ui/kit';
import { TaskDone, TaskShell } from '../TaskShell';
import type { SetupStatus } from '../../../lib/setupFlow';
import type { TaskRecord } from '../../../lib/setupTasks';

const SUPPORT_EMAIL = 'hello@foundlio.com';

const LOOK_FOR = [
  'Is the phone number right, everywhere it appears?',
  'Do the services listed match what you actually sell?',
  'Does the wording sound like you, or like a brochure?',
  'Anything missing that customers always ask about?',
];

export default function ReviewDraftTask({
  status,
  record,
  onBack,
  onComplete,
}: {
  status: SetupStatus;
  record?: TaskRecord;
  onBack: () => void;
  onComplete: (data: Record<string, string>) => void;
}) {
  const [mode, setMode] = useState<'idle' | 'changes'>('idle');
  const [notes, setNotes] = useState('');

  const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    `Change request ${status.orderRef} — ${status.businessName}`
  )}&body=${encodeURIComponent(notes)}`;

  if (record?.completedAt) {
    const approved = record.data?.outcome === 'approved';
    return (
      <TaskShell eyebrow="Step · your review" title={approved ? 'Approved.' : 'Changes are with us.'} onBack={onBack}>
        <TaskDone
          title={approved ? 'Straight to launch' : "We're on it"}
          detail={
            approved
              ? 'Nothing else needed from you. Next stop is your domain and going live.'
              : 'We make the changes and put the updated draft back in front of you. No limit on rounds.'
          }
          onBack={onBack}
        />
      </TaskShell>
    );
  }

  return (
    <TaskShell
      eyebrow="Step · your review"
      title="Read it like a customer would."
      description="Ten minutes here is worth more than anything else you can do for the site."
      onBack={onBack}
    >
      <Panel>
        <PanelBody className="space-y-3">
          <p className="text-sm leading-relaxed text-muted">
            Your draft is live at a private address. It is not indexed by Google and no one else has
            the link.
          </p>
          {status.previewUrl ? (
            <a
              href={status.previewUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-block rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
            >
              Open your draft
            </a>
          ) : (
            <p className="font-mono text-[11px] uppercase tracking-eyebrow text-zinc-400">
              Draft link appears as soon as the build finishes
            </p>
          )}
        </PanelBody>
      </Panel>

      <Panel>
        <PanelHeader title="Worth checking" />
        <PanelBody className="py-2">
          <ul>
            {LOOK_FOR.map((item) => (
              <li key={item} className="flex items-start gap-3 border-b border-zinc-200 py-3 last:border-b-0">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                <span className="text-sm leading-snug text-primary">{item}</span>
              </li>
            ))}
          </ul>
        </PanelBody>
      </Panel>

      {mode === 'idle' ? (
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => onComplete({ outcome: 'approved', summary: 'Approved the draft' })}
            className="rounded-full bg-accent px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
          >
            Looks good — go live
          </button>
          <button
            type="button"
            onClick={() => setMode('changes')}
            className="rounded-full border border-zinc-300 px-5 py-2.5 text-sm font-semibold text-primary transition-colors hover:border-primary"
          >
            Request changes
          </button>
        </div>
      ) : (
        <Panel>
          <PanelHeader title="What should change?" />
          <PanelBody className="space-y-3">
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={5}
              autoFocus
              placeholder={'e.g. Swap the hero photo for one of the truck.\nWe don\'t do septic work — please remove that service.'}
              className="w-full resize-none rounded-lg border border-zinc-300 bg-white px-3 py-2.5 text-sm text-primary outline-none transition-colors placeholder:text-zinc-400 focus:border-primary"
            />
            <div className="flex flex-wrap items-center gap-3">
              <a
                href={notes.trim() ? mailto : undefined}
                onClick={() => {
                  if (!notes.trim()) return;
                  onComplete({ outcome: 'changes', summary: 'Sent change notes' });
                }}
                className={
                  notes.trim()
                    ? 'rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90'
                    : 'pointer-events-none rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white opacity-40'
                }
              >
                Send to the team
              </a>
              <button
                type="button"
                onClick={() => setMode('idle')}
                className="font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500 transition-colors hover:text-primary"
              >
                Cancel
              </button>
            </div>
          </PanelBody>
        </Panel>
      )}
    </TaskShell>
  );
}
